import React, { useMemo, useState } from 'react';
import { Table, Button, Space, App, Tag, Popconfirm, Input, Select, Switch, Tooltip } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, MailOutlined } from '@ant-design/icons';
import { trpc } from '../../lib/trpc';
import UserFormModal, { type UserFormValues, type UserRole } from './UserFormModal';
import UserInviteModal, { type UserInviteValues } from './UserInviteModal';
import InvitationManagementContainer from './InvitationManagementContainer';
import { UserDisplay } from '../common';
import { getEffectiveAvatarUrl } from '../../utils/avatar';
import type { ColumnsType } from 'antd/es/table';
import { withDevModalFilePath } from '../common';

const { Search } = Input;
const { Option } = Select;

interface UserManagementTabProps {
  companyId?: string;
  currentUserRole: UserRole;
  currentUserId?: string;
}

interface User {
  id: string;
  email: string;
  name: string;
  role: UserRole;
  department?: string;
  position?: string;
  phone?: string;
  avatarUrl?: string;
  preferences?: Record<string, unknown> | null;
  isActive: boolean;
  emailVerified: boolean;
  lastLoginAt?: string;
  deactivatedAt?: string | null;
  deactivatedReason?: string | null;
  createdAt: string;
  updatedAt: string;
}

const { Modal } = withDevModalFilePath('client/src/components/user/UserManagementTab.tsx');

const UserManagementTab: React.FC<UserManagementTabProps> = ({
  companyId,
  currentUserRole,
  currentUserId,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [inviteModalVisible, setInviteModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState<UserRole | undefined>();
  const [statusFilter, setStatusFilter] = useState<boolean | undefined>();
  const utils = trpc.useUtils();
  const { message } = App.useApp();

  const isManager = ['admin', 'manager'].includes(currentUserRole);
  const isAdmin = currentUserRole === 'admin';

  const mapUserToFormValues = (user: User): UserFormValues => ({
    name: user.name,
    email: user.email,
    role: user.role,
    department: user.department ?? undefined,
    position: user.position ?? undefined,
    phone: user.phone ?? undefined,
    avatarUrl: getEffectiveAvatarUrl(user) ?? undefined,
  });

  const isCurrentUser = (user: User) => {
    if (!currentUserId) return false;
    return user.id === currentUserId;
  };

  // ユーザー一覧の取得
  const userListInput = useMemo(
    () => ({
      search: searchText || undefined,
      role: roleFilter,
      isActive: statusFilter,
      page: 1,
      limit: 50,
      ...(companyId ? { companyId } : {}),
    }),
    [companyId, roleFilter, searchText, statusFilter]
  );

  const { data: usersData, isLoading } = trpc.user.list.useQuery(userListInput, {
    enabled: Boolean(companyId),
  });

  const users = useMemo<User[]>(
    () => (Array.isArray(usersData?.data) ? (usersData.data as unknown as User[]) : []),
    [usersData]
  );

  // 有効な管理者の数をカウント
  const adminCount = useMemo(() => {
    return users.filter(user => user.role === 'admin' && user.isActive).length;
  }, [users]);

  // 編集対象が最後の管理者かどうか
  const isLastAdmin = useMemo(() => {
    if (!editingUser) return false;
    return editingUser.role === 'admin' && editingUser.isActive && adminCount === 1;
  }, [editingUser, adminCount]);

  // ユーザー作成
  const createMutation = trpc.user.create.useMutation({
    onSuccess: () => {
      message.success('ユーザーを作成しました');
      setModalVisible(false);
      setEditingUser(null);
      utils.user.list.invalidate();
    },
    onError: (error) => {
      message.error(`作成に失敗しました: ${error.message}`);
    },
  });

  // ユーザー更新
  const updateMutation = trpc.user.update.useMutation({
    onSuccess: () => {
      message.success('ユーザー情報を更新しました');
      setModalVisible(false);
      setEditingUser(null);
      utils.user.list.invalidate();
    },
    onError: (error) => {
      message.error(`更新に失敗しました: ${error.message}`);
    },
  });

  // ユーザー削除
  const deleteMutation = trpc.user.delete.useMutation({
    onSuccess: () => {
      message.success('ユーザーを削除しました');
      utils.user.list.invalidate();
    },
    onError: (error) => {
      message.error(`削除に失敗しました: ${error.message}`);
    },
  });

  // ユーザー招待
  const inviteMutation = trpc.user.invite.useMutation({
    onSuccess: () => {
      message.success('招待メールを送信しました');
      setInviteModalVisible(false);
      utils.user.list.invalidate();
    },
    onError: (error) => {
      message.error(`招待に失敗しました: ${error.message}`);
    },
  });

  const handleAdd = () => {
    setEditingUser(null);
    setModalVisible(true);
  };

  const handleInvite = () => {
    setInviteModalVisible(true);
  };

  const initialFormValues = editingUser ? mapUserToFormValues(editingUser) : null;

  const handleEdit = (user: User) => {
    setEditingUser(user);
    setModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    await deleteMutation.mutateAsync(id);
  };

  const handleStatusToggle = (user: User, nextActive: boolean) => {
    if (!canToggleStatus(user)) {
      return;
    }

    const executeUpdate = (reason?: string) => {
      return updateMutation.mutateAsync({
        id: user.id,
        data: nextActive
          ? { isActive: true }
          : {
              isActive: false,
              deactivatedReason: reason?.trim() || '管理者により無効化されました',
            },
      });
    };

    if (!nextActive) {
      Modal.confirm({
        title: 'ユーザーを無効化',
        content: `${user.name} (${user.email}) を無効化するとログインできなくなります。`,
        okText: '無効化',
        okButtonProps: { danger: true },
        cancelText: 'キャンセル',
        onOk: () => executeUpdate(),
      });
    } else {
      void executeUpdate();
    }
  };

  const handleModalSubmit = async (values: UserFormValues) => {
    if (editingUser) {
      await updateMutation.mutateAsync({
        id: editingUser.id,
        data: values,
      });
    } else {
      await createMutation.mutateAsync({
        ...values,
        companyId: companyId || '',
      });
    }
  };

  const handleInviteSubmit = async (values: UserInviteValues) => {
    await inviteMutation.mutateAsync({
      ...values,
      companyId: companyId || '',
    });
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin': return 'red';
      case 'manager': return 'orange';
      case 'user': return 'blue';
      case 'viewer': return 'gray';
      default: return 'default';
    }
  };

  const getRoleText = (role: string) => {
    switch (role) {
      case 'admin': return '管理者';
      case 'manager': return 'マネージャー';
      case 'user': return 'ユーザー';
      case 'viewer': return '閲覧者';
      default: return role;
    }
  };

  const canEditUser = (user: User) => {
    if (!isManager) return false;
    if (isAdmin) return true;
    // マネージャーは管理者を編集できない
    return user.role !== 'admin';
  };

  const canDeleteUser = (user: User) => {
    if (!isManager) return false;
    if (isCurrentUser(user)) return false;
    if (isAdmin) return true;
    // マネージャーは管理者を削除できない
    return user.role !== 'admin';
  };

  const canToggleStatus = (user: User) => {
    if (!isManager) return false;
    if (isAdmin) {
      return !isCurrentUser(user);
    }
    if (user.role === 'admin') return false;
    return !isCurrentUser(user);
  };

  const columns: ColumnsType<User> = [
    {
      title: 'ユーザー',
      key: 'user',
      width: 280,
      render: (_, record) => (
        <div>
          <UserDisplay
            user={{
              id: record.id,
              name: record.name,
              email: record.email,
              avatarUrl: record.avatarUrl ?? null,
              preferences: record.preferences ?? null,
            }}
            showAvatar
            showEmail
          />
          {!record.emailVerified && (
            <Tag color="orange" style={{ marginTop: 4 }}>未認証</Tag>
          )}
        </div>
      ),
    },
    {
      title: '権限 / 部署',
      key: 'roleDepartment',
      width: 180,
      render: (_, record) => {
        const roleDetails = [record.position, record.department].filter(Boolean).join(' / ');

        return (
          <div>
            <Tag color={getRoleColor(record.role)}>
              {getRoleText(record.role)}
            </Tag>
            <div className="text-xs text-gray-500 mt-1">
              {roleDetails || '-'}
            </div>
          </div>
        );
      },
    },
    {
      title: '連絡先',
      dataIndex: 'phone',
      key: 'phone',
      width: 120,
      render: (phone) => phone || '-',
    },
    {
      title: 'ステータス',
      key: 'status',
      width: 160,
      render: (_, record) => {
        const canChangeStatus = canToggleStatus(record);
        return (
          <div>
            <Space size="small">
              <Tag color={record.isActive ? 'green' : 'red'}>
                {record.isActive ? '有効' : '無効'}
              </Tag>
              {isManager && (
                <Tooltip
                  title={
                    canChangeStatus
                      ? record.isActive
                        ? 'クリックで無効化'
                        : 'クリックで有効化'
                      : 'ステータスを変更する権限がありません'
                  }
                >
                  <Switch
                    size="small"
                    checked={record.isActive}
                    onChange={(checked) => handleStatusToggle(record, checked)}
                    disabled={!canChangeStatus || updateMutation.isPending}
                  />
                </Tooltip>
              )}
            </Space>
            {record.lastLoginAt && (
              <div className="text-xs text-gray-500 mt-1">
                最終ログイン: {new Date(record.lastLoginAt).toLocaleDateString('ja-JP')}
              </div>
            )}
            {!record.isActive && record.deactivatedReason && (
              <div className="text-xs text-gray-500 mt-1">
                無効化理由: {record.deactivatedReason}
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: '作成日',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 100,
      render: (date) => new Date(date).toLocaleDateString('ja-JP'),
    },
    {
      title: '操作',
      key: 'action',
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            disabled={!canEditUser(record)}
            title="編集"
          />
          <Popconfirm
            title="削除確認"
            description="このユーザーを削除してもよろしいですか？"
            onConfirm={() => handleDelete(record.id)}
            okText="削除"
            cancelText="キャンセル"
            okButtonProps={{ danger: true }}
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              disabled={!canDeleteUser(record)}
              title="削除"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  if (!companyId) {
    return (
      <div>会社情報を読み込み中...</div>
    );
  }

  return (
    <div>
      {/* 検索・フィルター */}
      <div style={{ marginBottom: 16 }}>
        <Space wrap>
          <Search
            placeholder="名前またはメールアドレスで検索"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="権限で絞り込み"
            value={roleFilter}
            onChange={setRoleFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value="admin">管理者</Option>
            <Option value="manager">マネージャー</Option>
            <Option value="user">ユーザー</Option>
            <Option value="viewer">閲覧者</Option>
          </Select>
          <Select
            placeholder="ステータスで絞り込み"
            value={statusFilter}
            onChange={setStatusFilter}
            style={{ width: 150 }}
            allowClear
          >
            <Option value={true}>有効</Option>
            <Option value={false}>無効</Option>
          </Select>
        </Space>
      </div>

      {/* アクションボタン */}
      {isManager && (
        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAdd}
            >
              社内ユーザーを作成
            </Button>
            <Button
              icon={<MailOutlined />}
              onClick={handleInvite}
            >
              外部協力会社を招待
            </Button>
          </Space>
        </div>
      )}

      {/* ユーザー一覧テーブル */}
      <Table
        columns={columns}
        dataSource={users}
        rowKey="id"
        loading={isLoading}
        scroll={{ x: 900 }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `全 ${total} 件`,
        }}
        locale={{
          emptyText: 'ユーザーが登録されていません',
        }}
      />

      {isManager && companyId ? (
        <div style={{ marginTop: 32 }}>
          <InvitationManagementContainer companyId={companyId} />
        </div>
      ) : null}

      {/* ユーザー作成・編集モーダル */}
      <UserFormModal
        visible={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          setEditingUser(null);
        }}
        onSubmit={handleModalSubmit}
        initialValues={initialFormValues}
        loading={createMutation.isPending || updateMutation.isPending}
        isEdit={!!editingUser}
        currentUserRole={currentUserRole}
        isLastAdmin={isLastAdmin}
      />

      {/* ユーザー招待モーダル */}
      <UserInviteModal
        visible={inviteModalVisible}
        onCancel={() => setInviteModalVisible(false)}
        onSubmit={handleInviteSubmit}
        loading={inviteMutation.isPending}
        currentUserRole={currentUserRole}
      />
    </div>
  );
};

export default UserManagementTab;
